import { useDispatch, useSelector } from 'react-redux';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import TherapistSideBar from '../../common/TherapistSidebar/TherapistSidebar';
import { useState, useEffect } from 'react';
import { updateTherapist, getCurrentTherapist } from '../../redux/therapistSlice';
import { getBranches } from '../../redux/branchSlice'; 
import { getLanguages } from '../../redux/languageSlice';
import { getExpertises } from '../../redux/expertisesSelice';
import { useTranslation } from "react-i18next";

import { toast } from "react-toastify";

const TherapistSettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  // Redux state'i daha güvenli bir şekilde alın
  const branchState = useSelector((state) => state.branches);
  const languageState = useSelector((state) => state.languages);
  const therapistState = useSelector((state) => state.therapist) || {};
  const expertisesState = useSelector((state) => state.expertises);

  const { therapist, loading } = therapistState;
  const { expertises } = expertisesState;
  
  // States for form fields
  const [bio, setBio] = useState('');
  const [bioHtml, setBioHtml] = useState('');
  const [branchIds, setBranchIds] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [expertiseIds, setExpertiseIds] = useState([]);
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [expertiseDropdownOpen, setExpertiseDropdownOpen] = useState(false);
  const [certificateName, setCertificateName] = useState('');
  const [certificateDiscription, setCertificateDiscription] = useState('');
  const [certificates, setCertificates] = useState([]);
  
  // Education state
  const [education, setEducation] = useState([]);
  const [newSchool, setNewSchool] = useState('');
  const [newProgramDegree, setNewProgramDegree] = useState('');
  const [newFinishYear, setNewFinishYear] = useState('');

  // Experience state
  const [experience, setExperience] = useState([]);
  const [newExpTitle, setNewExpTitle] = useState('');
  const [newExpCompany, setNewExpCompany] = useState('');
  const [newExpStartYear, setNewExpStartYear] = useState('');
  const [newExpFinishYear, setNewExpFinishYear] = useState('');
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  const EDUCATION_PROGRAM_SEPARATOR = ' | ';

  const normalizeEducationItem = (edu = {}) => {
    const rawSchool = typeof edu.school === 'string' ? edu.school : '';
    const explicitProgram = edu.degree || edu.program || edu.department || '';

    if (explicitProgram) {
      return {
        ...edu,
        school: rawSchool,
        degree: explicitProgram
      };
    }

    if (rawSchool.includes(EDUCATION_PROGRAM_SEPARATOR)) {
      const splitIndex = rawSchool.indexOf(EDUCATION_PROGRAM_SEPARATOR);
      return {
        ...edu,
        school: rawSchool.slice(0, splitIndex).trim(),
        degree: rawSchool.slice(splitIndex + EDUCATION_PROGRAM_SEPARATOR.length).trim()
      };
    }

    return {
      ...edu,
      school: rawSchool,
      degree: ''
    };
  };
  
  useEffect(() => {
    dispatch(getCurrentTherapist());
    dispatch(getBranches());
    dispatch(getLanguages());
    dispatch(getExpertises());
  }, [dispatch]);

  // Validation function
  const validateForm = () => {
    const errors = {};
    
    // Biyografi kontrolü - HTML içerik kontrolü
    const stripHtml = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || "";
    };
    
    const bioText = stripHtml(bioHtml).trim();
    if (!bioText || bioText.length < 10) {
      errors.bio = t('biographyRequired');
    }
    
    // Branch kontrolü
    if (branchIds.length === 0) {
      errors.branches = t('branchRequired');
    }
    
    // Dil kontrolü
    if (languages.length === 0) {
      errors.languages = t('languageRequired');
    }
    
    // Uzmanlık alanı kontrolü
    if (expertiseIds.length === 0) {
      errors.expertises = t('expertiseRequired');
    }
    
    // Sertifika kontrolü
    if (certificates.length === 0) {
      errors.certificates = t('certificateRequired');
    }
    
    // Eğitim bilgisi kontrolü
    if (education.length === 0) {
      errors.education = t('educationRequired');
    }
    
    return errors;
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };
  
  // Quill formats
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'color', 'background'
  ];

  const handleBioChange = (content) => {
    setBio(content);
    // Sanitize HTML to prevent XSS attacks
    const sanitizedHtml = DOMPurify.sanitize(content);
    setBioHtml(sanitizedHtml);
    
    // Clear bio validation error when user types
    if (validationErrors.bio) {
      setValidationErrors(prev => ({ ...prev, bio: null }));
    }
  };

const toggleBranch = (branchId) => {
  // branchId'nin string olduğundan emin olun
  const id = typeof branchId === 'object' && branchId !== null ? branchId._id : branchId;
  
  if (branchIds.includes(id)) {
    setBranchIds(branchIds.filter(existingId => existingId !== id));
  } else {
    setBranchIds([...branchIds, id]);
  }
  
  // Clear branches validation error when user selects
  if (validationErrors.branches) {
    setValidationErrors(prev => ({ ...prev, branches: null }));
  }
};

const toggleExpertise = (expertiseId) => {
  // expertiseId'nin string olduğundan emin olun
  const id = typeof expertiseId === 'object' && expertiseId !== null ? expertiseId._id : expertiseId;
  
  if (expertiseIds.includes(id)) {
    setExpertiseIds(expertiseIds.filter(existingId => existingId !== id));
  } else {
    setExpertiseIds([...expertiseIds, id]);
  }
  
  // Clear expertises validation error when user selects
  if (validationErrors.expertises) {
    setValidationErrors(prev => ({ ...prev, expertises: null }));
  }
};

// useEffect'te therapist verilerini yüklerken de düzeltin
useEffect(() => {
  if (therapist) {
    if (therapist.bio) {
      setBio(therapist.bio);
      setBioHtml(therapist.bio);
    }
    
    // branchIds'leri string olarak sakla
    if (Array.isArray(therapist.branchIds) && therapist.branchIds.length > 0) {
      const cleanBranchIds = therapist.branchIds.map(branch => 
        typeof branch === 'object' && branch !== null ? branch._id : branch
      );
      setBranchIds(cleanBranchIds);
    }
    
    // expertiseIds'leri string olarak sakla
    if (therapist.expertisesTags) {
      if (Array.isArray(therapist.expertisesTags)) {
        const cleanExpertiseIds = therapist.expertisesTags.map(tag => 
          typeof tag === 'object' && tag !== null ? (tag._id || tag.name || tag) : tag
        );
        setExpertiseIds(cleanExpertiseIds);
      }
    }
    
    // Languages'ı object olarak sakla (çünkü UI'da name göstermek gerekiyor)
    if (therapist.languages) {
      if (Array.isArray(therapist.languages)) {
        const languageObjects = [];
        if (languageState && languageState.fetchedLanguages) {
          therapist.languages.forEach(langId => {
            const langIdStr = typeof langId === 'object' ? langId._id : langId;
            const foundLang = languageState.fetchedLanguages.find(l => l._id === langIdStr);
            if (foundLang) {
              languageObjects.push(foundLang);
            }
          });
        }
        setLanguages(languageObjects);
      }
    }
    
    // Certificates
    if (therapist.certificates && Array.isArray(therapist.certificates)) {
      setCertificates(therapist.certificates);
    }
    
    // Education
    if (therapist.education && Array.isArray(therapist.education)) {
      setEducation(therapist.education.map(normalizeEducationItem));
    }

    // Experience - only sync from backend if it has actual data
    // (empty array [] is truthy in JS and would wipe locally-added experience)
    if (Array.isArray(therapist.experience) && therapist.experience.length > 0) {
      setExperience(therapist.experience);
    }
  }
}, [therapist, languageState]);

  const toggleLanguage = (languageObj) => {
    // Eğer dil zaten languages listesinde varsa, çıkar
    if (languages.some(lang => lang._id === languageObj._id)) {
      setLanguages(languages.filter(lang => lang._id !== languageObj._id));
    } else {
      // Yoksa ekle
      setLanguages([...languages, languageObj]);
    }
    
    // Clear languages validation error when user selects
    if (validationErrors.languages) {
      setValidationErrors(prev => ({ ...prev, languages: null }));
    }
  };

  // Education functions
  const addEducation = () => {
    if (newSchool.trim() === '' || newProgramDegree.trim() === '' || !newFinishYear) { // Değiştirildi: newFinishDate -> newFinishYear
      toast.error(t('pleaseCompleteEducationInfo'));
      return;
    }
    
    const newEducationItem = {
      school: newSchool.trim(),
      degree: newProgramDegree.trim(),
      finish: newFinishYear // Değiştirildi: sadece yıl
    };
    
    setEducation([...education, newEducationItem]);
    toast.success(t('educationInfoAdded'));
    
    // Clear education validation error when user adds
    if (validationErrors.education) {
      setValidationErrors(prev => ({ ...prev, education: null }));
    }
    
    // Formu temizle
    setNewSchool('');
    setNewProgramDegree('');
    setNewFinishYear(''); // Değiştirildi: newFinishDate -> newFinishYear
  };
  
  const removeEducation = (index) => {
    setEducation(education.filter((_, i) => i !== index));
    toast.success(t('educationInfoRemoved'));
  };

  const addCertificate = () => {
    if (certificateName.trim() === '' || certificateDiscription.trim() === '') {
      toast.error(t('pleaseCompleteCertificateInfo'));
      return;
    }
    
    const newCertificateItem = {
      name: certificateName.trim(),
      description: certificateDiscription.trim()
    };
    
    setCertificates([...certificates, newCertificateItem]);
    toast.success(t('certificateAdded'));
    
    if (validationErrors.certificates) {
      setValidationErrors(prev => ({ ...prev, certificates: null }));
    }
    
    setCertificateName('');
    setCertificateDiscription('');
  };
  
  const removeCertificate = (index) => {
    setCertificates(certificates.filter((_, i) => i !== index));
    toast.success(t('certificateRemoved'));
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1969 }, (_, i) => currentYear - i);

  const addExperience = () => {
    if (!newExpTitle.trim() || !newExpCompany.trim() || !newExpStartYear) {
      toast.error(t('pleaseCompleteAllFields'));
      return;
    }
    setExperience([...experience, {
      title: newExpTitle.trim(),
      company: newExpCompany.trim(),
      start: `${newExpStartYear}-01-01`,
      finish: newExpFinishYear ? `${newExpFinishYear}-01-01` : '',
    }]);
    setNewExpTitle('');
    setNewExpCompany('');
    setNewExpStartYear('');
    setNewExpFinishYear('');
  };

  const removeExperience = (index) => {
    setExperience(experience.filter((_, i) => i !== index));
  };

  const findBranchNameById = (branchId) => {
    const branch = branchState.branches.find(branch => branch._id === branchId);
    return branch ? branch.name : t('unknownBranch');
  };
  
  const findExpertiseNameById = (expertiseId) => {
    const expertiseList = Array.isArray(expertises?.data)
      ? expertises.data
      : Array.isArray(expertises)
      ? expertises
      : [];
    const expertise = expertiseList.find(exp => exp._id === expertiseId);
    return expertise ? expertise.name : t('unknownExpertise');
  };
  
  // Değiştirildi: Sadece yılı göster
  const formatYear = (finishValue) => {
    if (!finishValue) return '';
    // Eğer tam tarih formatında geliyorsa yılı çıkar
    if (finishValue.includes('-')) {
      return new Date(finishValue).getFullYear().toString();
    }
    // Zaten yıl formatındaysa direkt döndür
    return finishValue.toString();
  };

const saveSettings = () => {
  // Form validation
  const errors = validateForm();
  setValidationErrors(errors);
  
  if (Object.keys(errors).length > 0) {
    toast.error(t('pleaseCompleteAllFields'));
    return;
  }
  
  // branchIds'lerin string olduğundan emin olun
  const cleanBranchIds = branchIds.map(branch => {
    // Eğer obje ise _id'sini al, string ise olduğu gibi kullan
    return typeof branch === 'object' && branch !== null ? branch._id : branch;
  });

  // expertisesTags'lerin de string olduğundan emin olun
  const cleanExpertiseIds = expertiseIds.map(expertise => {
    return typeof expertise === 'object' && expertise !== null ? expertise._id : expertise;
  });

  // Sadece istenen alanları gönder
  const updateData = {
    bio: bioHtml,
    expertisesTags: cleanExpertiseIds, // String array olarak gönder
    certificates: certificates,
    languages: languages.map(lang => 
      typeof lang === 'object' && lang !== null ? lang._id : lang
    ), // String array olarak gönder
    branchIds: cleanBranchIds, // String array olarak gönder
    education: education.map((edu) => {
      const normalized = normalizeEducationItem(edu);
      return {
        ...normalized,
        school: normalized.school,
        degree: normalized.degree,
        program: normalized.degree,
        department: normalized.degree
      };
    }),
    experience,
  };
  
  console.log('Data to be sent:', updateData); // Debug için
  
  dispatch(updateTherapist(updateData))
    .then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        toast.success(t('therapistInfoUpdated'));
        dispatch(getCurrentTherapist());
        setValidationErrors({});
      } else {
        const errMsg = result.payload?.message || result.payload || result.error?.message || t('updateError');
        console.error("Update failed:", result);
        toast.error(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    })
    .catch(err => {
      console.error("Update error:", err);
      toast.error(t('updateError'));
    });
};

  // API'den gelen branch verileri
  const availableBranches = branchState.branches || [];
  const fetchedLanguages = languageState?.fetchedLanguages || [];
  const availableExpertises = Array.isArray(expertises?.data)
    ? expertises.data
    : Array.isArray(expertises)
    ? expertises
    : [];
  const languagesLoading = languageState?.loading || false;
  const branchesLoading = branchState.loading;
  const expertisesLoading = expertisesState?.loading || false;

  // Terapist verileri ve seçenekler yüklenirken loading göster
  const isInitialLoading = therapistState?.loading || branchState?.loading || languageState?.loading || expertisesState?.loading;

  return (
    <div className="min-h-screen">
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <div className="flex-shrink-0">
          <TherapistSideBar />
        </div>

        {/* Ana İçerik Alanı */}
        <div className="flex-1 min-w-0 p-3 sm:p-4 overflow-auto">
          <h1 className="text-[17px] sm:text-[24px] font-roboto text-text mb-7 flex items-center">
            <svg className="w-6 h-6 mr-2 text-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t('therapistSettings')}
          </h1>
  
          {isInitialLoading ? (
            <div className="w-full flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cards-border"></div>
              <span className="ml-3 text-gray-600">{t('loadingData')}</span>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">{t('specializationBranches')}</h2>
                
                <div className="relative mb-6">
                  <button 
                    type="button"
                    className={`bg-white border rounded-md px-4 py-2 w-full text-left flex justify-between items-center ${
                      validationErrors.branches ? 'border-red-500' : 'border-gray-300'
                    }`}
                    onClick={() => setBranchDropdownOpen(!branchDropdownOpen)}
                    disabled={branchesLoading}
                  >
                    <span>
                      {branchesLoading 
                        ? t('branchesLoading')
                        : branchIds.length > 0 
                          ? t('branchesSelected', { count: branchIds.length })
                          : t('selectBranch')}
                    </span>
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {validationErrors.branches && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.branches}</p>
                  )}
                
                  {branchDropdownOpen && !branchesLoading && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-cards-border rounded-[8px] shadow-lg max-h-60 overflow-auto">
                      {availableBranches.map((branch) => (
                        <div 
                          key={branch._id}
                          className="flex items-center px-4 py-2 hover:bg-background cursor-pointer"
                          onClick={() => toggleBranch(branch._id)}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-cards-border focus:ring-cards-border border-gray-300 rounded"
                            checked={branchIds.includes(branch._id)}
                            onChange={() => {}}
                          />
                          <span className="ml-2">{branch.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              
                  <div className="flex flex-wrap gap-2 mt-2">
                  {branchIds.map((branchId, index) => (
                    <span 
                      key={index} 
                      className="bg-background text-text text-sm font-medium px-2.5 py-0.5 rounded-full flex items-center"
                    >
                      {findBranchNameById(branchId)}
                      <button 
                        type="button" 
                        className="ml-1.5 text-cards-border hover:text-cards-border focus:outline-none"
                        onClick={() => toggleBranch(branchId)}
                      >
                        <span className="text-[14px] font-bold">×</span>
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">{t('biography')}</h2>
                <div className="mb-4">
                  <div className={`quill-editor-container notranslate ${validationErrors.bio ? 'border-red-500' : ''}`} translate="no">
                    <ReactQuill 
                      theme="snow"
                      modules={modules}
                      formats={formats}
                      value={bio}
                      onChange={handleBioChange}
                      placeholder={t('biographyPlaceholder')}
                    />
                  </div>
                  {validationErrors.bio && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.bio}</p>
                  )}
                </div>
              </div>
            
              {/* Eğitim bilgileri bölümü */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">{t('educationInfo')}</h2>
                
                <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">University</label>
                    <input
                      type="text"
                      className="w-full border border-cards-border rounded-md px-4 py-2 focus:ring-cards-border focus:border-cards-border"
                      placeholder="e.g. Harvard University"
                      value={newSchool}
                      onChange={(e) => setNewSchool(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Program/Degree</label>
                    <input
                      type="text"
                      className="w-full border border-cards-border rounded-md px-4 py-2 focus:ring-cards-border focus:border-cards-border"
                      placeholder="e.g. BSc Psychology"
                      value={newProgramDegree}
                      onChange={(e) => setNewProgramDegree(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Graduation Year</label>
                    <input
                      type="number"
                      min="1900"
                      max="2030"
                      className="w-full border border-cards-border rounded-md px-4 py-2 focus:ring-[#E5D3DD] focus:border-[#E5D3DD]"
                      placeholder="2024"
                      value={newFinishYear}
                      onChange={(e) => setNewFinishYear(e.target.value)}
                    />
                  </div>
                </div>
                
                <button
                  type="button"
                  className="mb-4 bg-button-bg text-white px-4 py-2 rounded-md hover:opacity-90 focus:outline-none flex items-center"
                  onClick={addEducation}
                >
                  <FiPlus className="mr-1" /> {t('addEducation')}
                </button>
                
                {/* Eğitim listesi */}
                {education.length > 0 ? (
                  <div className="border rounded-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            University
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Program/Degree
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Graduation Year
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {education.map((edu, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {edu.school}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {edu.degree || edu.program || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatYear(edu.finish)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button 
                                onClick={() => removeEducation(index)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <FiTrash2 />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-gray-500 text-center py-4">
                    {t('noEducationAdded')}
                  </div>
                )}
                
                {validationErrors.education && (
                  <p className="text-red-500 text-sm mt-2">{validationErrors.education}</p>
                )}
              </div>
            
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">{t('serviceLanguages')}</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="relative">
                      <button 
                        type="button"
                        className={`bg-white border rounded-md px-4 py-2 w-full text-left flex justify-between items-center ${
                          validationErrors.languages ? 'border-red-500' : 'border-gray-300'
                        }`}
                        onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                      >
                        <span>
                          {languages.length > 0 
                            ? t('languagesSelected', { count: languages.length })
                            : t('selectLanguage')}
                        </span>
                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {validationErrors.languages && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.languages}</p>
                      )}
                    
                      {languageDropdownOpen && !languagesLoading && (
                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 max-h-60 overflow-auto">
                          {fetchedLanguages.map((language) => (
                            <div 
                              key={language._id}
                              className="flex items-center px-4 py-2 hover:bg-background cursor-pointer"
                              onClick={() => toggleLanguage(language)}
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-text focus:text border-gray-300 rounded"
                                checked={languages.some(lang => lang._id === language._id)}
                                onChange={() => {}}
                              />
                              <span className="ml-2">{language.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  
                    <div className="flex flex-wrap gap-2 mt-2">
                      {languages.map((language, index) => (
                        <span 
                          key={index} 
                          className="bg-background text-text text-sm font-medium px-2.5 py-0.5 rounded-full flex items-center"
                        >
                          {language.name}
                          <button 
                            type="button" 
                            className="ml-1.5 text-cards-border hover:text-cards-border focus:outline-none"
                            onClick={() => toggleLanguage(language)}
                          >
                            <span className="text-[14px] font-bold">×</span>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Uzmanlık Alanları */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">{t('expertiseAreas')}</h2>
                
                <div className="mb-4">
                  <div className="relative">
                    <button 
                      type="button"
                      className={`bg-white border rounded-md px-4 py-2 w-full text-left flex justify-between items-center ${
                        validationErrors.expertises ? 'border-red-500' : 'border-gray-300'
                      }`}
                      onClick={() => setExpertiseDropdownOpen(!expertiseDropdownOpen)}
                      disabled={expertisesLoading}
                    >
                      <span>
                        {expertisesLoading 
                          ? t('expertisesLoading')
                          : expertiseIds.length > 0 
                            ? t('expertisesSelected', { count: expertiseIds.length })
                            : t('selectExpertise')}
                      </span>
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {validationErrors.expertises && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.expertises}</p>
                    )}
                  
                    {expertiseDropdownOpen && !expertisesLoading && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-[8px] shadow-lg max-h-60 overflow-auto">
                        {availableExpertises.map((expertise) => (
                          <div 
                            key={expertise._id}
                            className="flex items-center px-4 py-2 hover:bg-text-cards-border cursor-pointer"
                            onClick={() => toggleExpertise(expertise._id)}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4  border-gray-300 rounded"
                              checked={expertiseIds.includes(expertise._id)}
                              onChange={() => {}}
                            />
                            <span className="ml-2">{expertise.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                
                  <div className="flex flex-wrap gap-2 mt-2">
                    {expertiseIds.map((expertiseId, index) => (
                      <span 
                        key={index} 
                        className="bg-background text-primary text-sm font-medium px-2.5 py-0.5 rounded-full flex items-center"
                      >
                        {findExpertiseNameById(expertiseId)}
                        <button 
                          type="button" 
                          className="ml-1.5 text-primary hover:text-cards-border focus:outline-none"
                          onClick={() => toggleExpertise(expertiseId)}
                        >
                          <span className="material-icons text-[14px]">close</span>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sertifikalar */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">{t('certificates')}</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">{t('certificateName')}</label>
                    <input
                      type="text"
                      className={`w-full border rounded-md px-4 py-2 focus:ring-cards-border focus:border-cards-border ${
                        validationErrors.certificateName ? 'border-red-500' : 'border-cards-border'
                      }`}
                      placeholder={t('certificateNamePlaceholder')}
                      value={certificateName}
                      onChange={(e) => {
                        setCertificateName(e.target.value);
                        if (validationErrors.certificateName) {
                          setValidationErrors(prev => ({ ...prev, certificateName: null }));
                        }
                      }}
                    />
                    {validationErrors.certificateName && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.certificateName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">{t('certificateDescription')}</label>
                    <textarea
                      className={`w-full border rounded-md px-4 py-2 focus:ring-cards-border focus:border-cards-border resize-none ${
                        validationErrors.certificateDescription ? 'border-red-500' : 'border-cards-border'
                      }`}
                      rows="3"
                      placeholder={t('certificateDescriptionPlaceholder')}
                      value={certificateDiscription}
                      onChange={(e) => {
                        setCertificateDiscription(e.target.value);
                        if (validationErrors.certificateDescription) {
                          setValidationErrors(prev => ({ ...prev, certificateDescription: null }));
                        }
                      }}
                    />
                    {validationErrors.certificateDescription && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.certificateDescription}</p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="mt-4 mb-6 bg-button-bg text-white px-4 py-2 rounded-md hover:opacity-90 focus:outline-none flex items-center"
                  onClick={addCertificate}
                >
                  <FiPlus className="mr-1" /> {t('addCertificate')}
                </button>

                {/* Certificate List */}
                {certificates.length > 0 ? (
                  <div className="border rounded-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('certificateName')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('certificateDescription')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {certificates.map((cert, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cert.name}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {cert.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button 
                                onClick={() => removeCertificate(index)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <FiTrash2 />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-gray-500 text-center py-4">
                    {t('noCertificateAdded')}
                  </div>
                )}
                
                {validationErrors.certificates && (
                  <p className="text-red-500 text-sm mt-2">{validationErrors.certificates}</p>
                )}
              </div>

              {/* İş Deneyimi */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2">{t('workExperience')}</h2>

                <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">{t('jobTitle')}</label>
                    <input
                      type="text"
                      className="w-full border border-cards-border rounded-md px-4 py-2 focus:ring-cards-border focus:border-cards-border"
                      placeholder={t('jobTitlePlaceholder')}
                      value={newExpTitle}
                      onChange={(e) => setNewExpTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">{t('company')}</label>
                    <input
                      type="text"
                      className="w-full border border-cards-border rounded-md px-4 py-2 focus:ring-cards-border focus:border-cards-border"
                      placeholder={t('companyPlaceholder')}
                      value={newExpCompany}
                      onChange={(e) => setNewExpCompany(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">{t('startYear')}</label>
                    <select
                      className="w-full border border-cards-border rounded-md px-4 py-2 focus:ring-cards-border focus:border-cards-border"
                      value={newExpStartYear}
                      onChange={(e) => setNewExpStartYear(e.target.value)}
                    >
                      <option value="">{t('selectYear')}</option>
                      {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">{t('endYear')}</label>
                    <select
                      className="w-full border border-cards-border rounded-md px-4 py-2 focus:ring-cards-border focus:border-cards-border"
                      value={newExpFinishYear}
                      onChange={(e) => setNewExpFinishYear(e.target.value)}
                    >
                      <option value="">{t('present')}</option>
                      {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>

                <button
                  type="button"
                  className="mb-4 bg-button-bg text-white px-4 py-2 rounded-md hover:opacity-90 focus:outline-none flex items-center"
                  onClick={addExperience}
                >
                  <FiPlus className="mr-1" /> {t('addExperience')}
                </button>

                {experience.length > 0 ? (
                  <div className="border rounded-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobTitle')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('company')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('startYear')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('endYear')}</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {experience.map((exp, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 text-sm text-gray-900">{exp.title}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{exp.company}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{exp.start ? new Date(exp.start).getFullYear() : '-'}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{exp.finish ? new Date(exp.finish).getFullYear() : t('present')}</td>
                            <td className="px-6 py-4 text-right text-sm font-medium">
                              <button onClick={() => removeExperience(index)} className="text-red-600 hover:text-red-900">
                                <FiTrash2 />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-gray-500 text-center py-4">{t('noExperienceAdded')}</div>
                )}
              </div>

              {/* Kaydet Butonu */}
              <div className="flex justify-end mb-6">
                <button
                  type="button"
                  className="bg-gradient-to-l from-white to-cards-border text-white px-8 py-3 rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-cards-border focus:ring-offset-2 transition-all duration-200 font-medium"
                  onClick={saveSettings}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('saving')}
                    </div>
                  ) : (
                    t('saveSettings')
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TherapistSettings;
